import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError, apiClient } from '@/lib/api-client';
import { env } from '@/lib/env';
import { queryKeys } from '@/lib/query-keys';
import { useSession } from '@/lib/auth';
import type { CurrentUser } from '@/types/api';

/** The fake profile used by the dev-only auth bypass. Never reachable in a production build. */
export const BYPASS_USER: CurrentUser = {
  sub: 'local-dev-bypass',
  email: 'local-admin@seez.tech',
  name: 'Local Admin',
  role: 'admin',
};

/**
 * The authoritative profile — crucially the role — comes from the API, never from the token
 * claims. Admin UI is hidden for viewers as a courtesy; the API is what actually enforces it.
 */
export function useMeQuery(): UseQueryResult<CurrentUser, Error> {
  const session = useSession();
  // Folded to `false` by `vite build`, which lets the bundler drop BYPASS_USER entirely.
  const bypass = import.meta.env.DEV && env.authBypass;
  // Anonymous visitors ask too: `/me` answers on the unauthenticated mirror with
  // `role: 'anonymous'`, which is what tells the UI to offer Sign in rather than an account menu.
  // Gating this on `authenticated` left a disabled query pending forever, and a disabled query
  // never resolves — the app hung on "Loading your profile…" for everyone without a session.
  const enabled = session.status !== 'loading' && !bypass;

  return useQuery<CurrentUser, Error>({
    queryKey: queryKeys.me(session.status === 'authenticated'),
    queryFn: ({ signal }) => apiClient.getMe(signal),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.isUnauthorized || error.isForbidden)) return false;
      return failureCount < 2;
    },
    ...(bypass ? { initialData: BYPASS_USER } : {}),
  });
}
